/**
 * Phase 0 — Reference family (locked editorial baseline)
 * - Master Spec: INNERVOICE_MASTER_SYSTEM_SPECIFICATION.md v1.0 (“Approved Master Reference”).
 * - Used at runtime for BOTH `problemKey` === `rel_breakup_fresh_pain` AND `rel_breakup_fresh_pain_reference_18d`
 *   (see JourneyDayDna localBlueprint mapping).
 * - `version` must stay aligned with the Master Spec reference label (`reference_18d_v5`).
 * - Tri-locale: `heNode` stores HE lines; `en`/`es` slots mirror male HE until a dedicated EN/ES pass replaces them.
 */
import { PHASE1_REFERENCE_18_PROBLEM_KEY } from "./config";

function gendered(male, female = male, other = male) {
  return { male, female, other };
}

function heNode(male, female = male) {
  return {
    he: gendered(male, female),
    en: male,
    es: male,
  };
}

function titleNode(he) {
  return { he, en: he, es: he };
}

function continueCta() {
  return {
    primary: {
      label: { he: "המשך", en: "Continue", es: "Continuar" },
    },
  };
}

function finishCta() {
  return {
    primary: {
      label: { he: "עשיתי את זה", en: "I did it", es: "Lo hice" },
    },
  };
}

function textStep(id, body, ctas = continueCta()) {
  return { id, type: "text", body, ctas };
}

function audioStep(id, body, ctas = continueCta()) {
  return { id, type: "audio", body, ctas };
}

function writingStep(id, prompts) {
  return {
    id,
    type: "writing",
    body: { he: [], en: [], es: [] },
    prompts,
    ctas: continueCta(),
  };
}

function splitPromptNode(idBase, promptsNode) {
  const heMale = Array.isArray(promptsNode?.he?.male) ? promptsNode.he.male : [];
  const heFemale = Array.isArray(promptsNode?.he?.female) ? promptsNode.he.female : heMale;
  const en = Array.isArray(promptsNode?.en) ? promptsNode.en : heMale;
  const es = Array.isArray(promptsNode?.es) ? promptsNode.es : heMale;

  return heMale.map((prompt, index) =>
    writingStep(`${idBase}_${index + 1}`, {
      he: gendered(prompt, heFemale[index] || prompt),
      en: en[index] || prompt,
      es: es[index] || prompt,
    })
  );
}

function createDay(dayNumber, emotionalStage, title, content) {
  return {
    dayNumber,
    emotionalStage,
    title: titleNode(title),
    titleLine: titleNode(title),
    subtitle: { he: "", en: "", es: "" },
    screens: [
      textStep(`d${dayNumber}_opening`, content.opening),
      audioStep(`d${dayNumber}_mindfulness`, content.mindfulness),
      ...splitPromptNode(`d${dayNumber}_writing`, content.writing),
      textStep(`d${dayNumber}_closing`, content.closing),
      textStep(`d${dayNumber}_action`, content.action, finishCta()),
    ],
  };
}

const days = [
  createDay(1, "pause", "לעצור מול מה שכואב", {
    opening: heNode(
      [
        "אני שם לב שמשהו בי לא נרגע מאז שהיא הלכה.",
        "אני ממשיך את היום שלי…",
        "אבל זה מרגיש כאילו אני רק מעביר אותו.",
        "אני מדבר עם אנשים,",
        "עונה,",
        "מחייך לפעמים…",
        "אבל זה לא באמת אני שם.",
        "אני כל הזמן חוזר אליה בראש.",
        "לא בכוונה.",
        "פשוט פתאום היא שם.",
        "איזה רגע קטן…",
        "משהו שהיא הייתה אומרת…",
        "איך שהיא הייתה מסתכלת עליי.",
        "וזה מספיק.",
        "זה תופס אותי מבפנים",
        "ולא משחרר.",
        "אני אומר לעצמי שזה נגמר.",
        "אני באמת מנסה להבין את זה.",
        "אבל יש חלק בי",
        "שעדיין לא הסכים לזה.",
        "כאילו משהו בי מחכה",
        "שהיא תחזור.",
        "וזה הדבר שהכי קשה לי להודות בו.",
      ],
      [
        "אני שמה לב שמשהו בי לא נרגע מאז שהוא הלך.",
        "אני ממשיכה את היום שלי…",
        "אבל זה מרגיש כאילו אני רק מעבירה אותו.",
        "אני מדברת עם אנשים,",
        "עונה,",
        "מחייכת לפעמים…",
        "אבל זה לא באמת אני שם.",
        "אני כל הזמן חוזרת אליו בראש.",
        "לא בכוונה.",
        "פשוט פתאום הוא שם.",
        "איזה רגע קטן…",
        "משהו שהוא היה אומר…",
        "איך שהוא היה מסתכל עליי.",
        "וזה מספיק.",
        "זה תופס אותי מבפנים",
        "ולא משחרר.",
        "אני אומרת לעצמי שזה נגמר.",
        "אני באמת מנסה להבין את זה.",
        "אבל יש חלק בי",
        "שעדיין לא הסכים לזה.",
        "כאילו משהו בי מחכה",
        "שהוא יחזור.",
        "וזה הדבר שהכי קשה לי להודות בו.",
      ]
    ),
    mindfulness: heNode(
      [
        "רגע.",
        "אני לא מנסה להבין את הכול עכשיו.",
        "אני רק שם לב למה שקורה בי.",
      ],
      [
        "רגע.",
        "אני לא מנסה להבין את הכול עכשיו.",
        "אני רק שמה לב למה שקורה בי.",
      ]
    ),
    writing: heNode(
      [
        "מה הכי כואב לי עכשיו באמת?",
        "איזה רגע ממנה לא יוצא לי מהראש?",
        "מה אני עדיין מחכה שיקרה בינינו?",
      ],
      [
        "מה הכי כואב לי עכשיו באמת?",
        "איזה רגע ממנו לא יוצא לי מהראש?",
        "מה אני עדיין מחכה שיקרה בינינו?",
      ]
    ),
    closing: heNode(
      [
        "עצרתי רגע.",
        "זה לא מובן מאליו.",
      ],
      [
        "עצרתי רגע.",
        "זה לא מובן מאליו.",
      ]
    ),
    action: heNode(
      ["אני נשאר עוד רגע בשקט עם מה שעלה בי."],
      ["אני נשארת עוד רגע בשקט עם מה שעלה בי."]
    ),
  }),
  createDay(2, "pause", "להישאר עם מה שקשה", {
    opening: heNode(
      ["אני כבר לא מצליח להגיד שזה סתם.", "זה כואב בי באמת.", "אני מרגיש את זה בגוף."],
      ["אני כבר לא מצליחה להגיד שזה סתם.", "זה כואב בי באמת.", "אני מרגישה את זה בגוף."]
    ),
    mindfulness: heNode(
      ["אני מרגיש איך זה נתפס בחזה.", "יש רגעים שזה עולה לי לגרון.", "אני לא דוחף את זה ממני."],
      ["אני מרגישה איך זה נתפס בחזה.", "יש רגעים שזה עולה לי לגרון.", "אני לא דוחפת את זה ממני."]
    ),
    writing: heNode(
      [
        "מה הכי שורף בי עכשיו?",
        "איפה הגוף שלי מחזיק את זה?",
        "מה הכאב הזה אומר בי בלי מילים?",
      ],
      [
        "מה הכי שורף בי עכשיו?",
        "איפה הגוף שלי מחזיק את זה?",
        "מה הכאב הזה אומר בי בלי מילים?",
      ]
    ),
    closing: heNode(["נתתי לכאב להיות כאן.", "זה מספיק לרגע הזה."]),
    action: heNode(
      ["היום אני מניח את הטלפון לדקה אחת ונשאר עם עצמי."],
      ["היום אני מניחה את הטלפון לדקה אחת ונשארת עם עצמי."]
    ),
  }),
  createDay(3, "pain", "לא לברוח גם היום", {
    opening: heNode(
      ["אני קם לאותו כאב שכבר חיכה לי.", "הוא לא נעלם רק כי ניסיתי להתרחק."],
      ["אני קמה לאותו כאב שכבר חיכה לי.", "הוא לא נעלם רק כי ניסיתי להתרחק."]
    ),
    mindfulness: heNode(
      ["אני שם לב מה בי רוצה מיד לברוח.", "ראש אחד רץ, גוף אחד מתכווץ.", "אני עוצר לפני שאני שוב נעלם."],
      ["אני שמה לב מה בי רוצה מיד לברוח.", "ראש אחד רץ, גוף אחד מתכווץ.", "אני עוצרת לפני שאני שוב נעלמת."]
    ),
    writing: heNode(
      [
        "לאן אני בורח כשהכאב עולה?",
        "מה אני לא רוצה להרגיש שם?",
        "מה נשאר בי כשהרעש נרגע?",
      ],
      [
        "לאן אני בורחת כשהכאב עולה?",
        "מה אני לא רוצה להרגיש שם?",
        "מה נשאר בי כשהרעש נרגע?",
      ]
    ),
    closing: heNode(
      ["לא ברחתי לגמרי היום.", "זה קטן, אבל זה אמיתי."],
      ["לא ברחתי לגמרי היום.", "זה קטן, אבל זה אמיתי."]
    ),
    action: heNode(
      ["היום אני נשאר לשבת עוד רגע לפני שאני עובר לדבר הבא."],
      ["היום אני נשארת לשבת עוד רגע לפני שאני עוברת לדבר הבא."]
    ),
  }),
  createDay(4, "pattern", "לראות מה חוזר בי", {
    opening: heNode(["אותה מחשבה חוזרת.", "אותה נפילה פנימית נפתחת איתה."]),
    mindfulness: heNode(
      ["אני רואה איך הראש מתחיל להריץ.", "מיד אחריו הגוף נסגר.", "זה קורה מהר.", "הפעם אני רואה את זה."],
      ["אני רואה איך הראש מתחיל להריץ.", "מיד אחריו הגוף נסגר.", "זה קורה מהר.", "הפעם אני רואה את זה."]
    ),
    writing: heNode(
      [
        "מה מפעיל בי את הסרט הזה?",
        "מה אני עושה שוב כשזה מתחיל?",
        "איפה אני נעלם בתוך זה?",
      ],
      [
        "מה מפעיל בי את הסרט הזה?",
        "מה אני עושה שוב כשזה מתחיל?",
        "איפה אני נעלמת בתוך זה?",
      ]
    ),
    closing: heNode(
      ["ראיתי משהו שחוזר בי.", "זה כבר מספיק להיום."],
      ["ראיתי משהו שחוזר בי.", "זה כבר מספיק להיום."]
    ),
    action: heNode(
      ["היום אני עוצר לרגע אחד כשאני מרגיש את הסרט מתחיל."],
      ["היום אני עוצרת לרגע אחד כשאני מרגישה את הסרט מתחיל."]
    ),
  }),
  createDay(5, "pattern", "להכיר את המסלול", {
    opening: heNode(
      ["אני רואה שזה לא רק רגע.", "זה מסלול שאני מכיר כבר."],
      ["אני רואה שזה לא רק רגע.", "זה מסלול שאני מכירה כבר."]
    ),
    mindfulness: heNode(
      ["לפעמים מחשבה אחת מספיקה.", "הגוף כבר יודע להיכנס למתח.", "אני שם לב למסלול הזה מבפנים."],
      ["לפעמים מחשבה אחת מספיקה.", "הגוף כבר יודע להיכנס למתח.", "אני שמה לב למסלול הזה מבפנים."]
    ),
    writing: heNode(
      [
        "איזה משפט מפעיל אותי הכי מהר?",
        "מה הגוף שלי עושה לפני שאני שם לב?",
        "מה אני מספר לעצמי במסלול הזה?",
      ],
      [
        "איזה משפט מפעיל אותי הכי מהר?",
        "מה הגוף שלי עושה לפני שאני שמה לב?",
        "מה אני מספרת לעצמי במסלול הזה?",
      ]
    ),
    closing: heNode(
      ["יש לזה צורה.", "אני רואה אותה קצת."],
      ["יש לזה צורה.", "אני רואה אותה קצת."]
    ),
    action: heNode(
      ["היום אני כותב לי מילה אחת על הרגע שבו זה מתחיל."],
      ["היום אני כותבת לי מילה אחת על הרגע שבו זה מתחיל."]
    ),
  }),
  createDay(6, "fear", "לגעת בפחד שמתחת", {
    opening: heNode(["יש שם גם פחד.", "לא רק כאב.", "פחד אמיתי."]),
    mindfulness: heNode(
      ["אני שומע את המשפטים שהפחד זורק לי.", "הדופק קופץ איתם.", "אני לא בורח גם מזה."],
      ["אני שומעת את המשפטים שהפחד זורק לי.", "הדופק קופץ איתם.", "אני לא בורחת גם מזה."]
    ),
    writing: heNode(
      [
        "מה אני מפחד שיקרה אם אשאר עם זה?",
        "מה אני מפחד שזה אומר עליי?",
        "מה בי קופא כשהפחד עולה?",
      ],
      [
        "מה אני מפחדת שיקרה אם אשאר עם זה?",
        "מה אני מפחדת שזה אומר עליי?",
        "מה בי קופא כשהפחד עולה?",
      ]
    ),
    closing: heNode(
      ["הפחד כאן.", "אני לא מתרחק ממנו מיד."],
      ["הפחד כאן.", "אני לא מתרחקת ממנו מיד."]
    ),
    action: heNode(
      ["היום אני נושם נשימה אחת לפני שאני שוב נסגר."],
      ["היום אני נושמת נשימה אחת לפני שאני שוב נסגרת."]
    ),
  }),
  createDay(7, "fear", "לשמוע מה הפחד אומר", {
    opening: heNode(["לפחד הזה יש קול.", "הוא מדבר בי כאילו הוא אמת."]),
    mindfulness: heNode(
      ["אני מקשיב למה שהוא זורק לי בפנים.", "הלב מאמין לו מהר.", "אני נשאר עוד רגע לפני שאני בולע הכול."],
      ["אני מקשיבה למה שהוא זורק לי בפנים.", "הלב מאמין לו מהר.", "אני נשארת עוד רגע לפני שאני בולעת הכול."]
    ),
    writing: heNode(
      [
        "איזה משפט הפחד אומר לי?",
        "מתי אני הכי מאמין לו?",
        "מה אני מסתיר בגלל הפחד הזה?",
      ],
      [
        "איזה משפט הפחד אומר לי?",
        "מתי אני הכי מאמינה לו?",
        "מה אני מסתירה בגלל הפחד הזה?",
      ]
    ),
    closing: heNode(
      ["אני שומע את הפחד.", "אני לא נבלע בו מיד."],
      ["אני שומעת את הפחד.", "אני לא נבלעת בו מיד."]
    ),
    action: heNode(
      ["היום אני אומר לעצמי בשקט משפט אחד עדין אחרי שהפחד מדבר."],
      ["היום אני אומרת לעצמי בשקט משפט אחד עדין אחרי שהפחד מדבר."]
    ),
  }),
  createDay(8, "inner_world", "לפגוש את החלק שנשבר", {
    opening: heNode(
      ["יש בי חלק שנשבר מזה יותר עמוק.", "לא רק אדם שנפרד.", "גם משהו בפנים שנבהל מאוד."],
      ["יש בי חלק שנשבר מזה יותר עמוק.", "לא רק אישה שנפרדה.", "גם משהו בפנים שנבהל מאוד."]
    ),
    mindfulness: heNode(
      ["אני מרגיש את החלק הקטן שנדרך.", "הוא לא מדבר בקול גדול.", "אבל אני מרגיש אותו חזק."],
      ["אני מרגישה את החלק הקטן שנדרך.", "הוא לא מדבר בקול גדול.", "אבל אני מרגישה אותו חזק."]
    ),
    writing: heNode(
      [
        "איזה חלק בי הכי נבהל מכל זה?",
        "בן כמה הוא מרגיש עכשיו?",
        "מה הוא היה אומר אילו היה לו קול?",
      ],
      [
        "איזה חלק בי הכי נבהל מכל זה?",
        "בת כמה היא מרגישה עכשיו?",
        "מה היא הייתה אומרת אילו היה לה קול?",
      ]
    ),
    closing: heNode(
      ["פגשתי חלק קטן שנבהל.", "אני נשאר לידו."],
      ["פגשתי חלק קטן שנבהל.", "אני נשארת לידה."]
    ),
    action: heNode(
      ["היום אני מניח יד על החזה ונותן לחלק הזה עוד רגע."],
      ["היום אני מניחה יד על החזה ונותנת לחלק הזה עוד רגע."]
    ),
  }),
  createDay(9, "inner_world", "להיות עם הלבד", {
    opening: heNode(["יש בי גם לבד.", "לבד שלא תמיד הודיתי בו."]),
    mindfulness: heNode(
      ["אני מרגיש את השקט הזה בבית ובגוף.", "לפעמים הוא דוקר.", "לפעמים הוא פשוט יושב בי כבד."],
      ["אני מרגישה את השקט הזה בבית ובגוף.", "לפעמים הוא דוקר.", "לפעמים הוא פשוט יושב בי כבד."]
    ),
    writing: heNode(
      [
        "איזה לבד נפתח בי עכשיו?",
        "מתי הלבד הזה הכי חזק?",
        "מה אני עושה כדי לא לפגוש אותו?",
      ],
      [
        "איזה לבד נפתח בי עכשיו?",
        "מתי הלבד הזה הכי חזק?",
        "מה אני עושה כדי לא לפגוש אותו?",
      ]
    ),
    closing: heNode(
      ["יש בי לבד אמיתי.", "אני לא מסתיר אותו."],
      ["יש בי לבד אמיתי.", "אני לא מסתירה אותו."]
    ),
    action: heNode(
      ["היום אני יושב בשקט עוד רגע אחד בלי להפעיל שום רעש."],
      ["היום אני יושבת בשקט עוד רגע אחד בלי להפעיל שום רעש."]
    ),
  }),
  createDay(10, "inner_world", "לשמוע את מה שהיה חסר", {
    opening: heNode(
      ["אני מתחיל לשמוע שלא רק איבדתי.", "משהו בי גם היה רעב לזה הרבה זמן."],
      ["אני מתחילה לשמוע שלא רק איבדתי.", "משהו בי גם היה רעב לזה הרבה זמן."]
    ),
    mindfulness: heNode(
      ["הלב מחפש משהו להישען עליו.", "הגוף מרגיש את החוסר הזה.", "אני נשאר לידו בלי להתבייש."],
      ["הלב מחפש משהו להישען עליו.", "הגוף מרגיש את החוסר הזה.", "אני נשארת לידו בלי להתבייש."]
    ),
    writing: heNode(
      [
        "מה היה חסר לי בתוך הקשר הזה?",
        "מה חיפשתי שוב ושוב ולא קיבלתי?",
        "מה אני עדיין מחפש עכשיו?",
      ],
      [
        "מה היה חסר לי בתוך הקשר הזה?",
        "מה חיפשתי שוב ושוב ולא קיבלתי?",
        "מה אני עדיין מחפשת עכשיו?",
      ]
    ),
    closing: heNode(
      ["משהו בי היה חסר גם קודם.", "עכשיו אני שומע את זה."],
      ["משהו בי היה חסר גם קודם.", "עכשיו אני שומעת את זה."]
    ),
    action: heNode(
      ["היום אני שותה משהו חם לאט ושם לב למה חסר לי עכשיו."],
      ["היום אני שותה משהו חם לאט ושמה לב למה חסר לי עכשיו."]
    ),
  }),
  createDay(11, "inner_world", "לגעת בבושה", {
    opening: heNode(
      ["יש בי גם בושה.", "משהו בי מיד רוצה להצטמצם.", "לא להיראות ככה."],
      ["יש בי גם בושה.", "משהו בי מיד רוצה להצטמצם.", "לא להיראות ככה."]
    ),
    mindfulness: heNode(
      ["אני מרגיש איפה הבושה מכווצת אותי.", "היא רוצה שאסתיר הכול.", "אני רק שם לב אליה בלי להיעלם."],
      ["אני מרגישה איפה הבושה מכווצת אותי.", "היא רוצה שאסתיר הכול.", "אני רק שמה לב אליה בלי להיעלם."]
    ),
    writing: heNode(
      [
        "על מה אני מתבייש עכשיו?",
        "מה אני לא רוצה שיראו בי?",
        "מה אני אומר לעצמי כשאני מתבייש בי?",
      ],
      [
        "על מה אני מתביישת עכשיו?",
        "מה אני לא רוצה שיראו בי?",
        "מה אני אומרת לעצמי כשאני מתביישת בי?",
      ]
    ),
    closing: heNode(
      ["הבושה כאן.", "אני נשאר איתה עוד רגע."],
      ["הבושה כאן.", "אני נשארת איתה עוד רגע."]
    ),
    action: heNode(
      ["היום אני מרכך את הכתפיים ונושם לאט פעם אחת."],
      ["היום אני מרככת את הכתפיים ונושמת לאט פעם אחת."]
    ),
  }),
  createDay(12, "need", "לשמוע מה מבקש מקום", {
    opening: heNode(["יש בי גם צורך.", "לא משהו גדול.", "משהו פשוט יותר."]),
    mindfulness: heNode(
      ["אני מרגיש את החלק בי שרק רוצה מקום.", "לא הסבר ולא הוכחה.", "פשוט מקום."],
      ["אני מרגישה את החלק בי שרק רוצה מקום.", "לא הסבר ולא הוכחה.", "פשוט מקום."]
    ),
    writing: heNode(
      [
        "מה חסר לי בפנים עכשיו?",
        "מה הלב שלי מבקש ולא מקבל?",
        "מה אני רוצה לתת לעצמי היום?",
      ],
      [
        "מה חסר לי בפנים עכשיו?",
        "מה הלב שלי מבקש ולא מקבל?",
        "מה אני רוצה לתת לעצמי היום?",
      ]
    ),
    closing: heNode(
      ["יש בי צורך אמיתי.", "אני נותן לו מקום."],
      ["יש בי צורך אמיתי.", "אני נותנת לו מקום."]
    ),
    action: heNode(
      ["היום אני נותן לעצמי דבר קטן שמרגיש כמו מקום."],
      ["היום אני נותנת לעצמי דבר קטן שמרגיש כמו מקום."]
    ),
  }),
  createDay(13, "perspective", "לראות את התמונה קצת אחרת", {
    opening: heNode(
      ["משהו בתמונה זז קצת.", "לא הסבר גדול.", "משהו מתחיל להתחבר."],
      ["משהו בתמונה זז קצת.", "לא הסבר גדול.", "משהו מתחיל להתחבר."]
    ),
    mindfulness: heNode(
      ["אני נושם רגע לפני שאני שוב מאשים את עצמי.", "אני נותן לעוד אפשרות להיות כאן."],
      ["אני נושמת רגע לפני שאני שוב מאשימה את עצמי.", "אני נותנת לעוד אפשרות להיות כאן."]
    ),
    writing: heNode(
      [
        "מה אולי לא ראיתי עד הסוף?",
        "מה אני מתחיל לראות קצת אחרת?",
        "איפה הייתי קשה עם עצמי מדי?",
      ],
      [
        "מה אולי לא ראיתי עד הסוף?",
        "מה אני מתחילה לראות קצת אחרת?",
        "איפה הייתי קשה עם עצמי מדי?",
      ]
    ),
    closing: heNode(
      ["משהו נפתח קצת.", "אני נשאר עם זה."],
      ["משהו נפתח קצת.", "אני נשארת עם זה."]
    ),
    action: heNode(
      ["היום אני מסתכל החוצה לדקה אחת ונותן לראש להתרחב קצת."],
      ["היום אני מסתכלת החוצה לדקה אחת ונותנת לראש להתרחב קצת."]
    ),
  }),
  createDay(14, "perspective", "להפסיק לשים הכול עליי", {
    opening: heNode(
      ["לא הכול היה עליי.", "אני יודע כמה מהר אני שם עליי את כל האשמה."],
      ["לא הכול היה עליי.", "אני יודעת כמה מהר אני שמה עליי את כל האשמה."]
    ),
    mindfulness: heNode(
      ["אני מרגיש איך הגוף מיד מתכווץ כשאני מאשים אותי.", "אני בודק את זה רגע.", "לא כדי לזכות את עצמי.", "רק כדי לראות ישר."],
      ["אני מרגישה איך הגוף מיד מתכווץ כשאני מאשימה אותי.", "אני בודקת את זה רגע.", "לא כדי לזכות את עצמי.", "רק כדי לראות ישר."]
    ),
    writing: heNode(
      [
        "מה אני שם עליי ישר?",
        "מה באמת היה שלי שם?",
        "ומה לא היה רק עליי?",
      ],
      [
        "מה אני שמה עליי ישר?",
        "מה באמת היה שלי שם?",
        "ומה לא היה רק עליי?",
      ]
    ),
    closing: heNode(
      ["לא הכול עליי.", "אני נותן לזה לשקוע."],
      ["לא הכול עליי.", "אני נותנת לזה לשקוע."]
    ),
    action: heNode(
      ["היום אני אומר בקול שקט משפט אחד אמיתי שלא כולו אשמה."],
      ["היום אני אומרת בקול שקט משפט אחד אמיתי שלא כולו אשמה."]
    ),
  }),
  createDay(15, "perspective", "להבין מה באמת נגמר", {
    opening: heNode(
      ["אולי לא רק הקשר נגמר.", "אולי נגמר גם משהו שהחזקתי הרבה זמן בפנים."],
      ["אולי לא רק הקשר נגמר.", "אולי נגמר גם משהו שהחזקתי הרבה זמן בפנים."]
    ),
    mindfulness: heNode(
      ["אני מרגיש גם סוף וגם ריק.", "שניהם יחד.", "אני לא ממהר למלא את זה."],
      ["אני מרגישה גם סוף וגם ריק.", "שניהם יחד.", "אני לא ממהרת למלא את זה."]
    ),
    writing: heNode(
      [
        "מה באמת נגמר לי כאן?",
        "מה אני עדיין מחזיק למרות שזה כבר נגמר?",
        "מה אני לא רוצה לקחת איתי הלאה?",
      ],
      [
        "מה באמת נגמר לי כאן?",
        "מה אני עדיין מחזיקה למרות שזה כבר נגמר?",
        "מה אני לא רוצה לקחת איתי הלאה?",
      ]
    ),
    closing: heNode(["משהו נגמר.", "אני לא מחזיק בו בכוח."], ["משהו נגמר.", "אני לא מחזיקה בו בכוח."]),
    action: heNode(
      ["היום אני מפנה כמה רגעים שקטים בלי לנסות למלא אותם מיד."],
      ["היום אני מפנה כמה רגעים שקטים בלי לנסות למלא אותם מיד."]
    ),
  }),
  createDay(16, "strength", "להישאר איתי", {
    opening: heNode(
      ["יש בי קצת יותר כוח להישאר.", "לא כוח של הצגה.", "כוח שקט."],
      ["יש בי קצת יותר כוח להישאר.", "לא כוח של הצגה.", "כוח שקט."]
    ),
    mindfulness: heNode(
      ["הגוף עוד זוכר.", "הלב עוד רגיש.", "אבל אני כבר לא נבהל מכל גל שעולה."],
      ["הגוף עוד זוכר.", "הלב עוד רגיש.", "אבל אני כבר לא נבהלת מכל גל שעולה."]
    ),
    writing: heNode(
      [
        "מה בי קצת יותר שקט היום?",
        "איך זה מרגיש כשאני נשאר איתי?",
        "איזה קול פנימי מחזיק אותי היום?",
      ],
      [
        "מה בי קצת יותר שקט היום?",
        "איך זה מרגיש כשאני נשארת איתי?",
        "איזה קול פנימי מחזיק אותי היום?",
      ]
    ),
    closing: heNode(
      ["יש בי קצת יותר מקום.", "אני נשאר איתו."],
      ["יש בי קצת יותר מקום.", "אני נשארת איתו."]
    ),
    action: heNode(
      ["היום אני הולך כמה דקות לאט, בלי למהר לשום מקום."],
      ["היום אני הולכת כמה דקות לאט, בלי למהר לשום מקום."]
    ),
  }),
  createDay(17, "strength", "לסמוך על הקול הפנימי", {
    opening: heNode(
      ["אני שומע בתוכי קול קצת יותר נקי.", "הוא לא צועק.", "אבל הוא נשאר."],
      ["אני שומעת בתוכי קול קצת יותר נקי.", "הוא לא צועק.", "אבל הוא נשאר."]
    ),
    mindfulness: heNode(
      ["אני שם לב איך הגוף נרגע קצת לידו.", "הוא לא מוחק את הכאב.", "הוא רק לא משאיר אותי לבד בתוכו."],
      ["אני שמה לב איך הגוף נרגע קצת לידו.", "הוא לא מוחק את הכאב.", "הוא רק לא משאיר אותי לבד בתוכו."]
    ),
    writing: heNode(
      [
        "איזה משפט פנימי אני רוצה לשמוע שוב?",
        "מתי הכי קל לי לשמוע את הקול הזה?",
        "מה קורה בי כשאני מקשיב לו?",
      ],
      [
        "איזה משפט פנימי אני רוצה לשמוע שוב?",
        "מתי הכי קל לי לשמוע את הקול הזה?",
        "מה קורה בי כשאני מקשיבה לו?",
      ]
    ),
    closing: heNode(
      ["יש בי קול שקט.", "אני שומע אותו."],
      ["יש בי קול שקט.", "אני שומעת אותו."]
    ),
    action: heNode(
      ["היום אני כותב לעצמי משפט אחד קצר שאני רוצה לקחת איתי."],
      ["היום אני כותבת לעצמי משפט אחד קצר שאני רוצה לקחת איתי."]
    ),
  }),
  createDay(18, "continuation", "להמשיך מכאן", {
    opening: heNode(
      ["אני לא מסיים סיפור.", "אני רק ממשיך מכאן עם יותר אמת ממה שהיה לי קודם."],
      ["אני לא מסיימת סיפור.", "אני רק ממשיכה מכאן עם יותר אמת ממה שהיה לי קודם."]
    ),
    mindfulness: heNode(
      ["המחשבות עוד יבואו.", "גם הכאב עוד יחזור לפעמים.", "אבל יש בי עכשיו מישהו שנשאר איתי."],
      ["המחשבות עוד יבואו.", "גם הכאב עוד יחזור לפעמים.", "אבל יש בי עכשיו מישהי שנשארת איתי."]
    ),
    writing: heNode(
      [
        "מה אני רוצה לזכור מעצמי?",
        "איך אני רוצה לחזור אליי כשהכול שוב עולה?",
        "מה אני רוצה להשאיר חי בי?",
      ],
      [
        "מה אני רוצה לזכור מעצמי?",
        "איך אני רוצה לחזור אליי כשהכול שוב עולה?",
        "מה אני רוצה להשאיר חי בי?",
      ]
    ),
    closing: heNode(
      ["אני ממשיך מכאן איתי."],
      ["אני ממשיכה מכאן איתי."]
    ),
    action: heNode(
      ["היום אני שומר לי רגע שקט אחד רק בשבילי."],
      ["היום אני שומרת לי רגע שקט אחד רק בשבילי."]
    ),
  }),
];

export const relBreakupFreshPainReference18Blueprint = {
  id: "rel_breakup_fresh_pain_reference_18d",
  version: "reference_18d_v5",
  problemKey: PHASE1_REFERENCE_18_PROBLEM_KEY,
  journeyKey: PHASE1_REFERENCE_18_PROBLEM_KEY,
  category: "relationships",
  durationDays: 18,
  totalDays: 18,
  phase1: true,
  journeyName: {
    he: "כאב פרידה טרייה",
    en: "The pain of a fresh breakup",
    es: "El dolor de una ruptura reciente",
  },
  ui: {
    phaseOneMode: true,
    wholeDayMode: false,
    showStageIndicators: false,
    showProgressText: false,
    allowSkipAlways: false,
    allowBack: true,
    hideDayCount: false,
    entryLabel: { he: "היום", en: "Today", es: "Hoy" },
    primaryCtaDefault: { he: "המשך", en: "Continue", es: "Continuar" },
    secondaryCtaDefault: { he: "", en: "", es: "" },
  },
  days,
  languages: {
    he: { days },
    en: { days },
    es: { days },
  },
};

export default relBreakupFreshPainReference18Blueprint;
